/**
 * Trader-native bot spawn, embedded in the Value Dashboard trade ticket.
 *
 * BotModeSwitch wraps the manual order form with a mode-row
 * [Manual | Vanilla] (+ [Pair MM] for managers/admins on a game with both
 * sibling Kalshi markets). The Recycler tab was parked when the queue-recycle
 * story moved to Rita (/bots/rita); this surface becomes an alternate Rita
 * interface in a later phase. It renders the row ONLY when the trader
 * has bot access AND there's a Kalshi market to spawn on — otherwise the
 * children (the existing order form) render byte-for-byte unchanged. Default
 * mode is Manual, so the existing flow is untouched until a trader opts in.
 *
 * A bot always deploys in GUARD mode (quote_for_rebate) — presence that avoids
 * fills. Letting it take fills ("let it ride") is a deliberate runtime toggle on
 * a claimed bot, in the Position Tracker.
 *
 * Pair MM is a DIFFERENT engine from the Vanilla tab: it adds the game's
 * away+home pair to the in-relay vanilla MM slate (POST /api/vanilla/games,
 * the two-leg sum-pair bot behind /bots/vanilla) — nothing goes to /api/edge.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Bot, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { apiFetch, useAuthStore } from '@/stores/authStore';
import { useBotAccess } from '@/hooks/useBotAccess';
import { useGrantPendingReason, useGrantSlot } from '@/hooks/useGrantSlot';
import {
  kindForMode,
  sizeFieldForKind,
  spawnBot,
  claimBot,
  type TraderBotMode,
} from '@/lib/bots/edge';
import { resolveBotConfig, type BotKindConfig } from '@/lib/bots/botDefaults';
import { specForKind } from '@/lib/bots/configSpec';

type Mode = 'manual' | TraderBotMode | 'pairmm';

/** #1114 T8: quick references on the usable mode tabs (Pair MM has none; not in the contract). */
const MODE_TITLE: Partial<Record<Mode, string>> = {
  manual: 'Manual: the normal order ticket.',
  vanilla: 'Vanilla: deploy a market-making bot on this market.',
};

/** The game's sibling Kalshi pair, for the Pair MM (in-relay sum-pair) mode. */
export interface PairMmTarget {
  eventTicker: string;
  tickerAway: string;
  tickerHome: string;
  exchangeIndexAway?: number;
  exchangeIndexHome?: number;
  awayCode: string;
  homeCode: string;
}

export function BotModeSwitch({
  ticker,
  pair,
  children,
}: {
  ticker: string | null;
  pair?: PairMmTarget | null;
  children: ReactNode;
}) {
  const botAccess = useBotAccess();
  const role = useAuthStore((s) => s.user?.role);
  const operator = role === 'manager' || role === 'admin';
  // A bot-access trader OR an operator role (manager/admin) can drive the bot
  // flow from the dashboard — same gate as the Position-Tracker BotZones, so an
  // org-floating admin isn't locked out of the trader-native spawn surface.
  const canSpawn = botAccess || operator;
  // A restored bot grant keeps the mode row in place, bot tabs disabled,
  // until this session confirms it.
  const botSlot = useGrantSlot('botAccess');
  const rowShown = (operator || botSlot !== 'hidden') && !!ticker;
  const pendingReason = useGrantPendingReason();
  const [mode, setMode] = useState<Mode>('manual');
  const activeMode: Mode = canSpawn ? mode : 'manual';

  const modes: { value: Mode; label: string }[] = [
    { value: 'manual', label: 'Manual' },
    { value: 'vanilla', label: 'Vanilla' },
  ];
  // Pair MM drives the manager-gated in-relay pair bot (/api/vanilla routes) —
  // a plain bot-access trader's POST would 403, so the tab is operator-only.
  if (pair && operator) {
    modes.push({ value: 'pairmm', label: 'Pair MM' });
  }

  // One tree shape in every state, so the manual ticket (children) is never
  // remounted — and its draft never lost — when access resolves. Without bot
  // access/role or a Kalshi market the row is absent and the ticket unchanged.
  return (
    <div>
      {rowShown && (
        <div className="border-border flex border-b">
          {modes.map((m) => {
            const usable = m.value === 'manual' || canSpawn;
            return (
              <button
                key={m.value}
                type="button"
                disabled={!usable}
                title={usable ? MODE_TITLE[m.value] : pendingReason}
                onClick={() => { if (usable) setMode(m.value); }}
                className={cn(
                  'flex-1 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wider transition-colors',
                  activeMode === m.value
                    ? 'text-foreground border-foreground/60 border-b-2'
                    : usable
                      ? 'text-muted-foreground hover:text-foreground'
                      : 'text-muted-foreground/50',
                )}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      )}
      {activeMode === 'manual' || !ticker ? (
        children
      ) : activeMode === 'pairmm' ? (
        <PairMmForm pair={pair!} />
      ) : (
        <BotSpawnForm key={activeMode} ticker={ticker} mode={activeMode} />
      )}
    </div>
  );
}

/** Pair MM: add this game's away+home pair to today's in-relay vanilla MM
 * slate. The relay validates the pair against the live Kalshi event and
 * windows it NOW → game start (or a fallback duration when the event has no
 * start-time anchor — the input appears only when the relay asks). */
function PairMmForm({ pair }: { pair: PairMmTarget }) {
  const [busy, setBusy] = useState(false);
  const [needsDuration, setNeedsDuration] = useState(false);
  const [durationMin, setDurationMin] = useState('120');
  const [status, setStatus] = useState<
    null | { kind: 'ok' } | { kind: 'exists' } | { kind: 'error'; msg: string }
  >(null);

  const deploy = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const res = await apiFetch('/api/vanilla/games', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          eventTicker: pair.eventTicker,
          tickerAway: pair.tickerAway,
          tickerHome: pair.tickerHome,
          awayCode: pair.awayCode || undefined,
          homeCode: pair.homeCode || undefined,
          ...(needsDuration && Number.isFinite(Number(durationMin))
            ? { fallbackDurationMin: Number(durationMin) }
            : {}),
        }),
      });
      if (res.ok) {
        setStatus({ kind: 'ok' });
      } else if (res.status === 409) {
        setStatus({ kind: 'exists' });
      } else {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        const msg = body?.error ?? `HTTP ${res.status}`;
        if (msg.includes('fallbackDurationMin')) setNeedsDuration(true);
        setStatus({ kind: 'error', msg });
      }
    } catch (err) {
      setStatus({ kind: 'error', msg: (err as Error).message });
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3 p-3">
      <div>
        <div className="text-foreground text-sm font-semibold">
          {pair.awayCode} @ {pair.homeCode}
        </div>
        <div className="text-muted-foreground font-mono text-[11px]">{pair.eventTicker}</div>
      </div>
      <p className="text-muted-foreground text-[11px]">
        Adds this game's away+home pair to today's Vanilla MM slate (the in-relay two-leg
        sum-pair bot). It quotes only while the day is ARMED on{' '}
        <Link to="/bots/vanilla" className="text-primary underline">
          /bots/vanilla
        </Link>
        , where size/caps/sum are set day-wide.
      </p>
      {needsDuration && (
        <label className="block">
          <span className="text-muted-foreground text-[11px]">
            No start-time anchor found — quote for (minutes)
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={5}
            max={720}
            value={durationMin}
            onChange={(e) => setDurationMin(e.target.value)}
            className="bg-background border-border text-foreground focus:border-primary mt-1 w-full rounded border px-2 py-1.5 text-sm focus:outline-none"
          />
        </label>
      )}
      {status?.kind === 'ok' || status?.kind === 'exists' ? (
        <div className="text-xs text-emerald-500">
          {status.kind === 'ok' ? 'Added to today’s slate.' : 'Already on today’s slate.'}{' '}
          <Link to="/bots/vanilla" className="underline">
            Open the Vanilla MM pane →
          </Link>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => void deploy()}
          className="bg-primary text-primary-foreground w-full rounded py-1.5 text-sm font-bold disabled:opacity-50"
        >
          {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Add to Vanilla MM slate'}
        </button>
      )}
      {status?.kind === 'error' && <p className="text-destructive text-xs">{status.msg}</p>}
    </div>
  );
}

function BotSpawnForm({ ticker, mode }: { ticker: string; mode: TraderBotMode }) {
  const personal = useAuthStore((s) => s.user?.botDefaults);
  const org = useAuthStore((s) => s.user?.orgBotDefaults);
  const kind = kindForMode(mode);
  const sizeField = sizeFieldForKind(kind);
  const spec = specForKind(kind);
  const resolved = useMemo(() => resolveBotConfig(kind, personal, org), [kind, personal, org]);

  const [override, setOverride] = useState<BotKindConfig>({});
  const config = useMemo(() => ({ ...resolved, ...override }), [resolved, override]);

  const allFields = useMemo(() => (spec?.sections ?? []).flatMap((s) => s.fields), [spec]);
  const builtinSize = allFields.find((f) => f.key === sizeField)?.builtin;
  const size = config[sizeField] ?? builtinSize ?? 1;
  const primaryKeys = useMemo(() => new Set(spec?.primary ?? []), [spec]);
  // Prominent guards = the kind's OTHER primary knobs (beyond size) — for the
  // vanilla bot these are its fill-avoidance triggers, the whole point of it —
  // surfaced up front rather than buried behind "Advanced".
  const guardFields = useMemo(
    () => allFields.filter((f) => f.key !== sizeField && primaryKeys.has(f.key)),
    [allFields, sizeField, primaryKeys],
  );
  // Advanced = the remaining non-orgOnly fields (not size, not a prominent guard).
  const advFields = useMemo(
    () =>
      (spec?.sections.filter((s) => !s.orgOnly) ?? [])
        .flatMap((s) => s.fields)
        .filter((f) => f.key !== sizeField && !primaryKeys.has(f.key)),
    [spec, sizeField, primaryKeys],
  );

  const [advanced, setAdvanced] = useState(false);
  // Two independent levers, each defaulted to the safe/sensible choice:
  //   ownership — org pool (default) vs claim to me
  //   mode      — guard / quote-for-rebate (default) vs let it ride / take fills
  const [claimMine, setClaimMine] = useState(false);
  const [ride, setRide] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<
    | null
    | { kind: 'ok' }
    | { kind: 'claimed' }
    | { kind: 'exists'; id?: string }
    | { kind: 'notConfigured' }
    | { kind: 'error'; msg?: string }
  >(null);

  const setField = (key: string, raw: string) => {
    setStatus(null);
    setConfirming(false);
    setOverride((o) => {
      const next = { ...o };
      if (raw.trim() === '') delete next[key];
      else {
        const n = Number(raw);
        if (Number.isFinite(n)) next[key] = n;
      }
      return next;
    });
  };

  const deploy = async () => {
    setBusy(true);
    setStatus(null);
    // Send the size the trader actually SEES (config value, else the kind's
    // built-in, else 1) explicitly, so the spawned bot can never run a different
    // size than the form showed — the recycler used to display 1 while the edge
    // ran its own default.
    const r = await spawnBot({
      ticker,
      kind,
      config: { ...config, [sizeField]: size },
      mode: ride ? 'cross_for_fills' : 'quote_for_rebate',
    });
    // Claim ONLY if the trader opted to take this bot personally. Default is the
    // org pool (unclaimed — anyone with access can claim it). Claiming is an
    // independent lever from let-it-ride; a claimed bot still starts in guard.
    if (r.ok && r.id && claimMine) await claimBot(r.id, ticker);
    setBusy(false);
    setConfirming(false);
    if (r.ok) setStatus({ kind: 'ok' });
    else if (r.code === 'BOT_EXISTS') setStatus({ kind: 'exists', id: r.existingId });
    else if (r.status === 503) setStatus({ kind: 'notConfigured' });
    else setStatus({ kind: 'error', msg: r.error });
  };

  const claimExisting = async (id: string) => {
    setBusy(true);
    const r = await claimBot(id, ticker);
    setBusy(false);
    setStatus(r.ok ? { kind: 'claimed' } : { kind: 'error', msg: r.error ?? 'Claim failed' });
  };

  return (
    <div className="space-y-3 p-3">
      <label className="block">
        <span className="text-muted-foreground text-[11px]">Contracts (per side)</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          value={config[sizeField] ?? ''}
          onChange={(e) => setField(sizeField, e.target.value)}
          placeholder={builtinSize !== undefined ? String(builtinSize) : '1'}
          className="bg-background border-border text-foreground focus:border-primary mt-1 w-full rounded border px-2 py-1.5 text-sm focus:outline-none"
        />
      </label>

      {guardFields.length > 0 && (
        <div>
          <span className="text-muted-foreground text-[11px]">Fill avoidance</span>
          <div className="mt-1 grid grid-cols-2 gap-2">
            {guardFields.map((f) => (
              <label key={f.key} className="block" title={f.help}>
                <span className="text-muted-foreground flex items-center gap-1 text-[10px]">
                  {f.label}
                  {f.unit ? <span className="opacity-60">{f.unit}</span> : null}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={f.nonNegative ? 0 : undefined}
                  value={config[f.key] ?? ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.builtin !== undefined ? String(f.builtin) : '—'}
                  className="bg-background border-border text-foreground focus:border-primary mt-0.5 w-full rounded border px-1.5 py-1 text-xs focus:outline-none"
                />
              </label>
            ))}
          </div>
        </div>
      )}

      <ConfigSummary kind={kind} config={config} ride={ride} />

      {/* Two independent levers as segmented button-pairs (matches the trade
          ticket's BUY YES/NO + LIMIT/MARKET toggles). Defaults: org pool, guard. */}
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setClaimMine(false)}
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              !claimMine ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            Org pool
          </button>
          <button
            type="button"
            onClick={() => setClaimMine(true)}
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              claimMine ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            Claim to me
          </button>
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setRide(false)}
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              !ride ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            Guard
          </button>
          <button
            type="button"
            onClick={() => setRide(true)}
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              ride ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            Let it ride
          </button>
        </div>
      </div>

      {advFields.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[11px]"
          >
            <ChevronDown size={12} className={advanced ? '' : '-rotate-90'} /> Advanced
          </button>
          {advanced && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {advFields.map((f) => (
                <label key={f.key} className="block" title={f.help}>
                  <span className="text-muted-foreground flex items-center gap-1 text-[10px]">
                    {f.label}
                    {f.unit ? <span className="opacity-60">{f.unit}</span> : null}
                  </span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={f.nonNegative ? 0 : undefined}
                    value={config[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.builtin !== undefined ? String(f.builtin) : '—'}
                    className="bg-background border-border text-foreground focus:border-primary mt-0.5 w-full rounded border px-1.5 py-1 text-xs focus:outline-none"
                  />
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {!confirming ? (
        <button
          onClick={() => setConfirming(true)}
          disabled={busy}
          className="bg-primary text-primary-foreground flex w-full items-center justify-center gap-1.5 rounded px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          <Bot size={15} /> Deploy Vanilla bot
        </button>
      ) : (
        <div className="space-y-2">
          <div className="text-muted-foreground text-[11px]">
            Deploy a {mode} bot at size {size},{' '}
            <span className={ride ? 'text-amber-400' : 'text-foreground'}>
              {ride ? 'let it ride (takes fills)' : 'guard mode (post-only)'}
            </span>
            , {claimMine ? 'claimed to you' : 'left in the org pool (anyone with access can claim it)'}?
          </div>
          <div className="flex gap-2">
            <button
              onClick={deploy}
              disabled={busy}
              className="bg-primary text-primary-foreground flex flex-1 items-center justify-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null} Confirm deploy
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="border-border text-muted-foreground rounded border px-3 py-1.5 text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {status?.kind === 'ok' && (
        <div className="text-xs text-emerald-500">
          {claimMine
            ? 'Bot deployed + claimed — manage it in My Bots.'
            : 'Bot deployed to the org pool — claim it from Org bots to take the wheel.'}
        </div>
      )}
      {status?.kind === 'claimed' && (
        <div className="text-xs text-emerald-500">Claimed — it's in your Position Tracker.</div>
      )}
      {status?.kind === 'notConfigured' && (
        <div className="text-xs text-amber-400">Edge engine isn't configured for this lane.</div>
      )}
      {status?.kind === 'error' && (
        <div className="text-xs text-red-400">{status.msg ?? 'Deploy failed'}</div>
      )}
      {status?.kind === 'exists' && (
        <div className="bg-muted/40 rounded p-2 text-xs">
          <div className="text-muted-foreground mb-1.5">A {mode} bot is already running on this market.</div>
          {status.id && (
            <button
              onClick={() => claimExisting(status.id as string)}
              disabled={busy}
              className="bg-primary text-primary-foreground rounded px-2.5 py-1 text-xs font-medium disabled:opacity-50"
            >
              Claim it
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ConfigSummary({ kind, config, ride }: { kind: string; config: BotKindConfig; ride: boolean }) {
  const modeLabel = ride ? 'let it ride' : 'guard';
  if (kind === 'vanillamm') {
    const width = (config.belowCents ?? 1) + (config.aboveCents ?? 2);
    const frontDistance = config.frontDistanceThreshold ?? 15000;
    const avoid = ['proximityRatioPct', 'frontDistanceThreshold', 'velocityPullCentsPerSec', 'depthVelocityPullPerSec', 'polyMoveCents'].some(
      (k) => (config[k] ?? (k === 'frontDistanceThreshold' ? frontDistance : 0)) > 0,
    );
    return (
      <div className="text-muted-foreground text-[10px]">
        {width}¢ wide · front {frontDistance.toLocaleString()} · avoid-fill {avoid ? 'on' : 'off'} · {modeLabel}
      </div>
    );
  }
  return <div className="text-muted-foreground text-[10px]">queue presence · {modeLabel}</div>;
}
